/**
 * MODULE 00 - WELCOME & LANDING.
 *
 * Built to docs/ux-specs/00-welcome-landing.md. The pre-authentication front door: Splash, Welcome,
 * the How-it-works carousel, the Sandbox notice, and the Language sheet. It moves no value and reads
 * no account data, and it lives OUTSIDE the app shell (no nav bars): an unauthenticated party is the
 * one visitor we can be certain is here, so this page holds nothing they should not see.
 *
 * Routing note: in the finished product the root `/` decides Splash -> (live session ? Home : Welcome).
 * That session-based root routing is finalised in Module 01 (Authentication). Here `/welcome` always
 * shows the landing so it stays reviewable while the backend is off.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Brandmark } from '@/components/brand';
import { Dialog, Sheet } from '@/components/overlay';
import { Reveal, Stagger, StaggerItem } from '@/components/motion';
import { ThemeToggle } from '@/components/theme-toggle';

const INTRO_SEEN_KEY = 'lipa.introSeen';
const LANG_KEY = 'lipa.lang';

export default function Welcome() {
  const [showSplash, setShowSplash] = useState(true);
  const [introOpen, setIntroOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  // Splash: hold the brand briefly, then reveal Welcome. Capped so a slow device never traps a user
  // on a logo (the spec's 2s ceiling).
  useEffect(() => {
    const t = setTimeout(() => setShowSplash(false), 1100);
    return () => clearTimeout(t);
  }, []);

  // First run on this device auto-shows the intro once; afterwards it is on-demand only.
  useEffect(() => {
    if (showSplash) return;
    try {
      if (!localStorage.getItem(INTRO_SEEN_KEY)) setIntroOpen(true);
    } catch {
      /* storage blocked: just skip the auto-intro */
    }
  }, [showSplash]);

  function closeIntro() {
    setIntroOpen(false);
    try {
      localStorage.setItem(INTRO_SEEN_KEY, '1');
    } catch {
      /* ignore */
    }
  }

  return (
    <>
      <AnimatePresence>{showSplash ? <Splash key="splash" /> : null}</AnimatePresence>

      {!showSplash ? (
        <WelcomeScreen
          onHowItWorks={() => setIntroOpen(true)}
          onSandbox={() => setNoticeOpen(true)}
          onLanguage={() => setLangOpen(true)}
        />
      ) : null}

      <AnimatePresence>{introOpen ? <IntroCarousel onDone={closeIntro} /> : null}</AnimatePresence>

      <SandboxNotice open={noticeOpen} onClose={() => setNoticeOpen(false)} />
      <LanguageSheet open={langOpen} onClose={() => setLangOpen(false)} />
    </>
  );
}

// ---------------------------------------------------------------------------
// 00 - Splash
// ---------------------------------------------------------------------------

function Splash() {
  return (
    <motion.div
      className="fixed inset-0 z-40 flex items-center justify-center bg-canvas"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      aria-busy="true"
    >
      <span className="sr-only">Loading VASP</span>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <Brandmark size="lg" />
      </motion.div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// 01 - Welcome (value proposition)
// ---------------------------------------------------------------------------

function WelcomeScreen({
  onHowItWorks,
  onSandbox,
  onLanguage,
}: {
  onHowItWorks: () => void;
  onSandbox: () => void;
  onLanguage: () => void;
}) {
  return (
    <div className="grid min-h-[calc(100svh-2.5rem)] lg:grid-cols-2">
      {/* LEFT: the pitch and the doors. */}
      <div className="flex flex-col justify-center px-5 py-10 sm:px-8">
        <div className="mx-auto w-full max-w-md">
          <Reveal>
            <Brandmark size="lg" />
          </Reveal>

          <Reveal delay={0.05}>
            <button
              onClick={onSandbox}
              className="mt-6 inline-flex items-center gap-1.5 rounded-pill border border-primary/20 bg-primary-tint px-3 py-1 text-xs font-medium text-brand transition hover:bg-primary/10"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              CMA Regulatory Sandbox
            </button>
          </Reveal>

          <Reveal delay={0.08}>
            <h1 className="mt-4 text-3xl font-semibold leading-tight tracking-tight text-ink sm:text-4xl">
              Money, virtual assets, and real-world assets. One wallet, built for compliance.
            </h1>
          </Reveal>

          <Reveal delay={0.12}>
            <p className="mt-3 text-ink-muted">
              Hold and move shillings and virtual assets, and invest in tokenized real-world assets,
              with verification and protection built in from the first step.
            </p>
          </Reveal>

          <Stagger className="mt-6 space-y-3">
            {TRUST_POINTS.map((t) => (
              <StaggerItem key={t.title}>
                <TrustPoint icon={t.icon} title={t.title} body={t.body} />
              </StaggerItem>
            ))}
          </Stagger>

          <div className="mt-8 space-y-3">
            <Link
              to="/"
              className="flex w-full items-center justify-center rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light"
            >
              Create account
            </Link>
            <Link
              to="/signin"
              className="flex w-full items-center justify-center rounded-field border border-border bg-surface py-3 text-sm font-medium text-ink transition hover:bg-surface-sunken"
            >
              I already have an account
            </Link>
            <Link
              to="/"
              className="flex w-full items-center justify-center py-1 text-sm font-medium text-brand hover:underline"
            >
              Register a business
            </Link>
          </div>

          {/* Footer utility row. */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-ink-muted">
            <button onClick={onHowItWorks} className="hover:text-ink">
              How it works
            </button>
            <button onClick={onLanguage} className="hover:text-ink">
              Language
            </button>
            <Link to="/support" className="hover:text-ink">
              Help
            </Link>
            <ThemeToggle className="rounded-lg p-1 text-ink-muted transition hover:text-ink" />
          </div>
        </div>
      </div>

      {/* RIGHT: a pine marketing panel. Desktop only, decorative, kept out of the a11y tree. */}
      <div
        aria-hidden
        className="relative hidden overflow-hidden bg-gradient-to-br from-primary to-primary-dark lg:block"
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full bg-primary-light/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-10 h-80 w-80 rounded-full bg-accent/20 blur-3xl" />
        <div className="relative flex h-full flex-col justify-center px-12 text-white">
          <p className="max-w-sm text-2xl font-semibold leading-snug tracking-tight">
            Africa&apos;s real-world assets, tokenized and within reach.
          </p>
          <p className="mt-3 max-w-sm text-white/70">
            Property, farms, energy and more, held in regulated trusts and owned in fractions from
            KES 1,000.
          </p>

          {/* A small floating balance card, echoing the in-app hero, as the visual anchor. */}
          <div className="mt-8 w-64 rounded-xl border border-white/15 bg-white/10 p-4 backdrop-blur">
            <p className="text-xs text-white/70">Available to spend</p>
            <p className="tabular mt-1 text-2xl font-semibold">KES 225,000.00</p>
            <div className="mt-3 flex gap-2">
              <span className="rounded-pill bg-white/15 px-2 py-0.5 text-nano font-medium">
                BTC
              </span>
              <span className="rounded-pill bg-white/15 px-2 py-0.5 text-nano font-medium">
                USDT
              </span>
              <span className="rounded-pill bg-white/15 px-2 py-0.5 text-nano font-medium">
                Land
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrustPoint({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-brand">
        {icon}
      </span>
      <div>
        <p className="text-sm font-medium text-ink">{title}</p>
        <p className="text-sm text-ink-muted">{body}</p>
      </div>
    </div>
  );
}

const TRUST_POINTS = [
  {
    title: 'Regulated and screened',
    body: 'Every person is verified and screened. Nobody transacts anonymously here.',
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.8}
          d="M12 3 4 6v5c0 4.5 3.2 8 8 10 4.8-2 8-5.5 8-10V6l-8-3Z"
        />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="m9 12 2 2 4-4" />
      </svg>
    ),
  },
  {
    title: 'Escrow on every movement',
    body: 'Your money is held while checks run, and returned to you if anything stops it.',
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.8}
          d="M7 11V8a5 5 0 0 1 10 0v3m-11 0h12a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
        />
      </svg>
    ),
  },
  {
    title: 'Disputes and compensation',
    body: 'Raise a dispute in the app. A compensation fund backs genuine platform failures.',
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.8}
          d="M8 10.5h8M8 14h5m-8.5 5.5 2.2-2.2A2 2 0 0 1 8.1 16.7H17a2.5 2.5 0 0 0 2.5-2.5v-6A2.5 2.5 0 0 0 17 5.7H7A2.5 2.5 0 0 0 4.5 8.2v11.3Z"
        />
      </svg>
    ),
  },
];

// ---------------------------------------------------------------------------
// 02 - How it works (intro carousel)
// ---------------------------------------------------------------------------

const SLIDES = [
  {
    title: 'One wallet, everything',
    body: 'Hold and move shillings and virtual assets, safely, in one place.',
    tone: 'from-primary to-primary-dark',
  },
  {
    title: 'Own real-world assets',
    body: 'Invest from KES 1,000 in property, farms and more, each held in a regulated trust.',
    tone: 'from-emerald-500 to-emerald-700',
  },
  {
    title: 'You are protected',
    body: 'Every movement is held in escrow, disputes are handled, and a compensation fund has your back.',
    tone: 'from-teal-500 to-teal-700',
  },
];

function IntroCarousel({ onDone }: { onDone: () => void }) {
  const [i, setI] = useState(0);
  const last = i === SLIDES.length - 1;
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Move focus to each new slide's heading, so a screen-reader or keyboard user follows along.
  useEffect(() => {
    headingRef.current?.focus();
  }, [i]);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col bg-canvas"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      role="dialog"
      aria-modal="true"
      aria-label="How VASP works"
    >
      <div className="flex justify-end p-4">
        <button
          onClick={onDone}
          className="rounded-field px-3 py-1.5 text-sm font-medium text-ink-muted hover:text-ink"
        >
          Skip
        </button>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-sm"
          >
            <div
              className={`mx-auto flex h-40 w-40 items-center justify-center rounded-3xl bg-gradient-to-br ${SLIDES[i].tone} shadow-lifted`}
            >
              <span className="text-5xl font-semibold text-white">{i + 1}</span>
            </div>
            <h2
              ref={headingRef}
              tabIndex={-1}
              className="mt-8 text-2xl font-semibold tracking-tight text-ink outline-none"
            >
              {SLIDES[i].title}
            </h2>
            <p className="mt-2 text-ink-muted">{SLIDES[i].body}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between p-6">
        <div
          className="flex gap-1.5"
          role="group"
          aria-label={`Slide ${i + 1} of ${SLIDES.length}`}
        >
          {SLIDES.map((_, n) => (
            <span
              key={n}
              aria-hidden
              className={`h-1.5 rounded-full transition-all ${n === i ? 'w-5 bg-primary' : 'w-1.5 bg-border'}`}
            />
          ))}
        </div>

        {last ? (
          <Link
            to="/"
            onClick={onDone}
            className="rounded-field bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light"
          >
            Get started
          </Link>
        ) : (
          <button
            onClick={() => setI((n) => Math.min(n + 1, SLIDES.length - 1))}
            className="rounded-field bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light"
          >
            Next
          </button>
        )}
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// 03 - Sandbox notice
// ---------------------------------------------------------------------------

function SandboxNotice({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} title="You are entering a regulatory sandbox">
      <div className="mt-2 space-y-2 text-sm leading-relaxed text-ink-muted">
        <p>
          VASP operates in the Capital Markets Authority (Kenya) regulatory sandbox. It is a
          supervised, experimental environment, not a fully licensed product.
        </p>
        <p>
          Transaction limits are capped, participation is by whitelist, and your money is held apart
          from the company&apos;s own funds.
        </p>
        <p>
          You will be asked to read and accept the full risk disclosures before you can hold or move
          value. Nothing here moves money until you do.
        </p>
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row-reverse">
        <button
          onClick={onClose}
          className="rounded-field bg-primary px-4 py-2.5 text-sm font-medium text-white transition hover:bg-primary-light"
        >
          Got it
        </button>
        <Link
          to="/disclosures"
          className="rounded-field border border-border px-4 py-2.5 text-center text-sm font-medium text-ink transition hover:bg-surface-sunken"
        >
          Read the full disclosures
        </Link>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// 04 - Language & region
// ---------------------------------------------------------------------------

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'sw', label: 'Kiswahili' },
];

function LanguageSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [lang, setLang] = useState('en');

  useEffect(() => {
    try {
      setLang(localStorage.getItem(LANG_KEY) ?? 'en');
    } catch {
      /* ignore */
    }
  }, [open]);

  function apply() {
    try {
      localStorage.setItem(LANG_KEY, lang);
    } catch {
      /* ignore */
    }
    onClose();
  }

  return (
    <Sheet open={open} onClose={onClose} title="Language and region">
      <fieldset className="space-y-1">
        <legend className="sr-only">Choose a language</legend>
        {LANGUAGES.map((l) => (
          <label
            key={l.code}
            className={`flex cursor-pointer items-center justify-between rounded-field border px-3 py-3 text-sm transition ${
              lang === l.code
                ? 'border-primary bg-primary-tint text-brand'
                : 'border-border text-ink hover:bg-surface-sunken'
            }`}
          >
            <span className="font-medium">{l.label}</span>
            <input
              type="radio"
              name="language"
              value={l.code}
              checked={lang === l.code}
              onChange={() => setLang(l.code)}
              className="accent-primary"
            />
          </label>
        ))}
      </fieldset>

      <p className="mt-3 px-1 text-xs text-ink-subtle">
        Region: Kenya. The sandbox is Kenya-only for now.
      </p>

      <button
        onClick={apply}
        className="mt-4 w-full rounded-field bg-primary py-2.5 text-sm font-medium text-white transition hover:bg-primary-light"
      >
        Apply
      </button>
    </Sheet>
  );
}
